import { useCallback, useState } from 'react'
import { navigate } from 'gatsby'
import { doFetch } from '../../rest/FetchService'
import { EXTERNAL_REFERRER, useUtmParameters, UTM_CAMPAIGN, UTM_CONTENT, UTM_MEDIUM, UTM_SOURCE, UTM_TERM, } from '../../cookies/utmTrackingLogic'

// Zoho Forms allows exporting a created Form as HTML+CSS.
//
// However, they do not allow control over what happens after Form submission- You are always redirected to a Zoho
// thank-you or error page, meaning visitor lands outside of our website.
// They also do not allow setting fieldnames for Fields, but generate them automatically eg 'SingleLine',
// 'SingleLine1', 'Checkbox', 'Checkbox1', etc.
//
// Therefore approach is to export the Form as HTML+CSS and use from it only the Form submission URL, plus their
// auto-generated fieldnames with our own component, which then handles form submission and ignores the Zoho responses/redirects.

type ZohoFormLogicType<T> = {
  formIdentifier: string
  zohoSubmitUrl: string
  successRedirectUrl: string
  submitErrorMessage?: string
  zohoFormFields: ZohoFormFields<T>
  onSubmit?: () => void
}

export type ZohoFormHandlers<T> = {
  values: Partial<T>
  errors: FormErrors<T>
  isFetching: boolean
  submitError: string
  setValue: (name: string, value: any) => void
  clearError: (name: string) => void
  doSubmit: () => void
  setError: (fieldName: string, error: string) => void
}

export type ZohoFormFields<T> = {
  [K in keyof T]: {
    zohoFieldName: string;
    label?: string;
    initialValue: T[K];
    required?: boolean;
    runEmailValidation?: boolean;
    customValidation?: {
      validationFn: (value: any) => boolean;
      errorMessage: string;
    }
  }
}

type FormErrors<T> = {
  [K in keyof T]?: string
}

const REQUIRED_VALIDATION_ERROR = (fieldLabel: any) =>
  `Please fill your ${fieldLabel}`;
const EMAIL_VALIDATION_ERROR = "Please enter a valid Email";
const isValidEmail = (email: any) => {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
    email
  )
};

export const useZohoFormLogic = <T>({
  formIdentifier,
  zohoSubmitUrl,
  successRedirectUrl,
  submitErrorMessage,
  zohoFormFields,
  onSubmit,
}: ZohoFormLogicType<T>): ZohoFormHandlers<T> => {
  const utmParams = useUtmParameters();

  // Extract initial values from Zoho Form Fields
  const initialValues: Partial<T> = {};
  let eachValueKey: keyof T;
  for (eachValueKey in zohoFormFields) {
    initialValues[eachValueKey] = zohoFormFields[eachValueKey].initialValue
  }

  // Set initial values and initial errors
  const [values, setValues] = useState<Partial<T>>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [isFetching, setFetching] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Handlers to be passed to Fields
  const setValue = (name: string, value?: string | boolean) => {
    setValues({
      ...values,
      [name]: value,
    })
  };

  const clearError = (name: string) => {
    setErrors({
      ...errors,
      [name]: undefined,
    })
  };

  const setError = useCallback((fieldName: string, error: string) => {
    setErrors(currentErrors => ({
      ...currentErrors,
      [fieldName]: error
    }));
  }, [setErrors]);

  // Validation
  const validateFormValues = (): boolean => {
    const validationErrors: FormErrors<T> = {};
    let hasError = false;

    for (eachValueKey in zohoFormFields) {

      const fieldConfig = zohoFormFields[eachValueKey];
      const fieldValue = values[eachValueKey];

      // Validate required fields
      if (fieldConfig.required) {
        if (!fieldValue) {
          hasError = true;
          validationErrors[eachValueKey] = REQUIRED_VALIDATION_ERROR(fieldConfig.label);
        }
      }

      // Validate email fields
      if (fieldConfig.runEmailValidation) {
        if (!isValidEmail(fieldValue)) {
          hasError = true;
          validationErrors[eachValueKey] = EMAIL_VALIDATION_ERROR
        }
      }

      // Custom validation
      if (fieldConfig.customValidation && !fieldConfig.customValidation.validationFn(values[eachValueKey])) {
        hasError = true;
        validationErrors[eachValueKey] = fieldConfig.customValidation.errorMessage;
      }
    }

    if (hasError) {
      setErrors(validationErrors);
      return false
    } else {
      return true
    }
  };

  // Create shape that Zoho is expecting
  const assembleFormData = (): FormData => {
    const formData = new FormData();
    // Required Zoho fields
    formData.append("zf_referrer_name", "");
    formData.append("zf_redirect_url", "");
    formData.append("zc_gad", "");

    // Identify which Form is submitting in Zoho + UTM tracking
    formData.append("SingleLine", formIdentifier);
    formData.append("SingleLine1", utmParams[EXTERNAL_REFERRER]);
    formData.append("SingleLine2", utmParams[UTM_SOURCE]);
    formData.append("SingleLine3", utmParams[UTM_CAMPAIGN]);
    formData.append("SingleLine4", utmParams[UTM_TERM]);
    formData.append("SingleLine5", utmParams[UTM_CONTENT]);
    formData.append("SingleLine6", utmParams[UTM_MEDIUM]);

    // Fields that User enters
    for (eachValueKey in zohoFormFields) {
      if (typeof values[eachValueKey] === "boolean") {
        if (values[eachValueKey]) {
          formData.append(zohoFormFields[eachValueKey].zohoFieldName, "on")
        }
      } else {
        formData.append(
          zohoFormFields[eachValueKey].zohoFieldName,
          String(values[eachValueKey])
        )
      }
    }

    return formData
  };

  const doZohoSubmit = () => {
    doFetch(zohoSubmitUrl, "POST", assembleFormData(), true)
      .then(() => {
        onSubmit ? onSubmit() : navigate(successRedirectUrl)
      })
      .catch(err => {
        console.error("Form submission error: ", err);
        setSubmitError(submitErrorMessage || '');
        setFetching(false)
      })
  };

  const doSubmit = () => {
    if (validateFormValues()) {
      setFetching(true);
      doZohoSubmit()
    }
  };

  return {
    values,
    errors,
    isFetching,
    submitError,
    setValue,
    clearError,
    doSubmit,
    setError,
  }
};
